import { MADEIRA_TZ } from '../../shared/types'

/** Intl formatters are expensive to build, so keep one per locale + shape. */
const cache = new Map<string, Intl.DateTimeFormat>()

function fmt(locale: string, key: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const id = `${locale}|${key}`
  let f = cache.get(id)
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...opts })
    cache.set(id, f)
  }
  return f
}

/** YYYY-MM-DD is a bare calendar date, so it is parsed as UTC midnight. */
function asDate(date: string): Date {
  return new Date(`${date}T00:00:00Z`)
}

export const weekdayShort = (date: string, locale: string) =>
  fmt(locale, 'wd-short', { weekday: 'short' }).format(asDate(date))

export const weekdayNarrow = (date: string, locale: string) =>
  fmt(locale, 'wd-narrow', { weekday: 'narrow' }).format(asDate(date))

export const dayOfMonth = (date: string, locale: string) =>
  fmt(locale, 'day', { day: 'numeric' }).format(asDate(date))

export const shortDate = (date: string, locale: string) =>
  fmt(locale, 'month-day', { day: 'numeric', month: 'short' }).format(asDate(date))

export function rangeLabel(from: string, to: string, locale: string): string {
  return `${shortDate(from, locale)} - ${shortDate(to, locale)}`
}

export function isWeekend(date: string): boolean {
  const dow = asDate(date).getUTCDay()
  return dow === 0 || dow === 6
}

export function formatPrice(price: number | null, currency: string, locale: string): string | null {
  if (price === null) return null
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: price % 1 === 0 ? 0 : 2,
  }).format(price)
}

export function formatClock(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  )
}

/** True when the visitor's own clock already matches the times we display. */
export function viewerIsInMadeira(): boolean {
  return Intl.DateTimeFormat().resolvedOptions().timeZone === MADEIRA_TZ
}
